import { ref } from 'vue'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'

function unwrapShipping(res) {
  const d = res?.data
  return {
    items: d && Array.isArray(d.items) ? d.items : [],
    ecpayLogistics: d?.ecpayLogistics ?? {
      enabled: false,
      mode: null,
      needsLogisticsType: false,
      typeMismatch: false,
    },
  }
}

function parseJson(str, fallback) {
  try {
    return JSON.parse(str)
  } catch {
    return fallback
  }
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** 優先順序數字小者在前；相同則依建立時間早者在前 */
function sortShippingRulesByPriority(items) {
  return [...items].sort((a, b) => {
    const pa = num(a.priority)
    const pb = num(b.priority)
    if (pa !== pb) return pa - pb
    const taRaw = a.created_at ?? a.createdAt
    const tbRaw = b.created_at ?? b.createdAt
    const ta = taRaw ? Date.parse(taRaw) : 0
    const tb = tbRaw ? Date.parse(tbRaw) : 0
    if (ta !== tb) return ta - tb
    return String(a.id ?? '').localeCompare(String(b.id ?? ''))
  })
}

function sortPromotionRulesByPriority(items) {
  return [...items].sort((a, b) => {
    const pa = num(a.priority)
    const pb = num(b.priority)
    if (pa !== pb) return pa - pb
    const taRaw = a.created_at ?? a.createdAt
    const tbRaw = b.created_at ?? b.createdAt
    const ta = taRaw ? Date.parse(taRaw) : 0
    const tb = tbRaw ? Date.parse(tbRaw) : 0
    if (ta !== tb) return ta - tb
    return String(a.id ?? '').localeCompare(String(b.id ?? ''))
  })
}

function formatDateInput(iso) {
  if (!iso) return ''
  const s = String(iso)
  return s.length >= 10 ? s.slice(0, 10) : ''
}

function toIsoDateStart(dateStr) {
  if (!dateStr || !String(dateStr).trim()) return null
  return `${String(dateStr).trim()}T00:00:00.000Z`
}

function toIsoDateEnd(dateStr) {
  if (!dateStr || !String(dateStr).trim()) return null
  return `${String(dateStr).trim()}T23:59:59.999Z`
}

function defaultPromoForm() {
  return {
    name: '',
    priority: 0,
    is_active: true,
    /** exclusive = 主規則（整單僅能套用一筆有折價的主規則）；stackable = 加算（可與其他加算規則疊加） */
    applicationMode: 'stackable',
    validFrom: '',
    validTo: '',
    usageMode: 'auto',
    promoCode: '',
    couponTemplateId: '',
    couponIssueMax: '',
    target: 'order',
    lineFilterMode: 'all',
    /** @type {{ variantId: string, productName: string, variantLabel: string, sku: string }[]} */
    selectedVariants: [],
    categoryId: '',
    includeCategoryDescendants: false,
    minQty: 0,
    discountType: 'fixed',
    discountAmount: 0,
    discountPercent: 0,
    minSpend: '',
  }
}

export function buildPromoConfig(form) {
  const appMode = form.applicationMode === 'exclusive' ? 'exclusive' : 'stackable'
  const cfg = {
    applicationMode: appMode,
    usageMode: form.usageMode || 'auto',
    promoCode: form.usageMode === 'fixed_code' ? String(form.promoCode || '').trim() : '',
    couponTemplateId: form.usageMode === 'coupon_template' ? (form.couponTemplateId || null) : null,
    couponIssueMax:
      form.usageMode === 'coupon_template' && form.couponIssueMax !== '' && form.couponIssueMax != null
        ? Math.max(0, Number(form.couponIssueMax))
        : null,
    target: form.target === 'lines' ? 'lines' : 'order',
    lineFilter: { mode: 'all' },
    minQty: Math.max(0, Number(form.minQty) || 0),
    discountType: form.discountType === 'percent' ? 'percent' : 'fixed',
    discountAmount: form.discountType === 'percent' ? 0 : Math.max(0, Number(form.discountAmount) || 0),
    discountPercent: form.discountType === 'percent' ? Math.max(0, Number(form.discountPercent) || 0) : 0,
    minSpend:
      form.minSpend === '' || form.minSpend == null || Number(form.minSpend) <= 0
        ? null
        : Math.max(0, Number(form.minSpend)),
  }
  if (form.lineFilterMode === 'variants') {
    const ids = (form.selectedVariants || []).map((v) => v.variantId).filter(Boolean)
    cfg.lineFilter = { mode: 'variants', variantIds: ids }
  } else if (form.lineFilterMode === 'category') {
    cfg.lineFilter = {
      mode: 'category',
      categoryId: form.categoryId || null,
      includeCategoryDescendants: Boolean(form.includeCategoryDescendants),
    }
  }
  return cfg
}

function ruleToPromoForm(rule) {
  const c = parseRuleConfig(rule.config)
  const lf = c.lineFilter || c.line_filter || {}
  let lineFilterMode = 'all'
  let selectedVariants = []
  let categoryId = ''
  let includeCategoryDescendants = false
  if (lf.mode === 'variants') {
    lineFilterMode = 'variants'
    const ids = lf.variantIds || lf.variant_ids || []
    selectedVariants = ids.map((id) => ({
      variantId: String(id),
      productName: '',
      variantLabel: String(id).slice(0, 8) + '…',
      sku: '',
    }))
  } else if (lf.mode === 'category') {
    lineFilterMode = 'category'
    categoryId = lf.categoryId || lf.category_id || ''
    includeCategoryDescendants = Boolean(lf.includeCategoryDescendants || lf.include_category_descendants)
  }
  const appModeRaw = c.applicationMode ?? c.application_mode
  const applicationMode = String(appModeRaw).toLowerCase() === 'exclusive' ? 'exclusive' : 'stackable'
  return {
    name: rule.name || '',
    priority: rule.priority ?? 0,
    is_active: rule.is_active !== false,
    applicationMode,
    validFrom: formatDateInput(rule.valid_from ?? rule.validFrom),
    validTo: formatDateInput(rule.valid_to ?? rule.validTo),
    usageMode: c.usageMode || c.usage_mode || 'auto',
    promoCode: c.promoCode || c.promo_code || '',
    couponTemplateId: c.couponTemplateId || c.coupon_template_id || '',
    couponIssueMax: c.couponIssueMax ?? c.coupon_issue_max ?? '',
    target: c.target === 'lines' ? 'lines' : 'order',
    lineFilterMode,
    selectedVariants,
    categoryId,
    includeCategoryDescendants,
    minQty: c.minQty ?? c.min_qty ?? 0,
    discountType: c.discountType === 'percent' || c.discount_type === 'percent' ? 'percent' : 'fixed',
    discountAmount: c.discountAmount ?? c.discount_amount ?? 0,
    discountPercent: c.discountPercent ?? c.discount_percent ?? 0,
    minSpend: c.minSpend != null || c.min_spend != null ? String(c.minSpend ?? c.min_spend ?? '') : '',
  }
}

function buildShippingConfig(form) {
  const rt = form.rule_type
  if (rt === 'flat_rate') return { fee: num(form.fee) }
  if (rt === 'threshold_free') {
    return { threshold_amount: num(form.threshold_amount), fee: num(form.fee) }
  }
  if (rt === 'volumetric') return { rate_per_kg: num(form.rate_per_kg) }
  if (rt === 'dimensional') return { rate_per_m3: num(form.rate_per_m3) }
  if (rt === 'max_edge') return { rate_per_cm: num(form.rate_per_cm) }
  return {}
}

function parseRuleConfig(config) {
  if (config == null) return {}
  if (typeof config === 'object') return config
  if (typeof config === 'string') {
    try {
      return JSON.parse(config)
    } catch {
      return {}
    }
  }
  return {}
}

function unwrapList(res) {
  const d = res?.data
  if (d && Array.isArray(d.items)) return d.items
  return []
}

function isEcpayManagedRule(rule) {
  const config = parseRuleConfig(rule?.config)
  return config.managedBy === 'ecpay'
}

function ruleToShipForm(rule) {
  const c = parseRuleConfig(rule.config)
  const managed = isEcpayManagedRule(rule)
  return {
    name: rule.name || '',
    rule_type: rule.rule_type || 'flat_rate',
    priority: rule.priority ?? 0,
    fee: managed
      ? (c.fee === null || c.fee === undefined || c.fee === '' ? '' : c.fee)
      : num(c.fee ?? 60),
    threshold_amount: num(c.threshold_amount ?? 999),
    rate_per_kg: num(c.rate_per_kg ?? 0),
    rate_per_m3: num(c.rate_per_m3 ?? 0),
    rate_per_cm: num(c.rate_per_cm ?? 0),
    is_active: rule.is_active !== false,
  }
}

function defaultShipForm() {
  return {
    name: '',
    rule_type: 'flat_rate',
    priority: 0,
    fee: 60,
    threshold_amount: 999,
    rate_per_kg: 0,
    rate_per_m3: 0,
    rate_per_cm: 0,
    is_active: true,
  }
}

export function useCommerceAdvancedRules() {
  const loadError = ref('')
  const saving = ref(false)
  const shippingRules = ref([])
  const ecpayLogistics = ref({
    enabled: false,
    mode: null,
    needsLogisticsType: false,
    typeMismatch: false,
  })
  const promoRules = ref([])
  const b2bPrices = ref([])
  const tierCredits = ref([])

  const shipForm = ref(defaultShipForm())
  const shippingDragIndex = ref(null)
  const shippingDragOverIndex = ref(null)
  /** 運費規則編輯中列 id；null 表示新增模式 */
  const editingShippingId = ref(null)
  const promoForm = ref(defaultPromoForm())
  const editingPromoId = ref(null)
  const b2bForm = ref({ variantId: '', memberTier: '', unitPrice: 0 })

  async function reload(options = {}) {
    loadError.value = ''
    try {
      const [s, p, b, c] = await Promise.all([
        commerceSettingsApi.listShippingRules(options.ensureEcpayShipping ? { ensure: '1' } : undefined),
        commerceSettingsApi.listPromotionRules(),
        commerceSettingsApi.listB2bTierPrices(),
        commerceSettingsApi.listMemberTierCredits(),
      ])
      const shipping = unwrapShipping(s)
      shippingRules.value = sortShippingRulesByPriority(shipping.items)
      ecpayLogistics.value = shipping.ecpayLogistics
      promoRules.value = sortPromotionRulesByPriority(unwrapList(p))
      b2bPrices.value = unwrapList(b)
      tierCredits.value = unwrapList(c)
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message || '載入失敗'
    }
  }

  async function reloadB2bPrices() {
    loadError.value = ''
    try {
      const b = await commerceSettingsApi.listB2bTierPrices()
      b2bPrices.value = unwrapList(b)
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message || '載入失敗'
    }
  }

  async function reloadTierCredits() {
    loadError.value = ''
    try {
      const c = await commerceSettingsApi.listMemberTierCredits()
      tierCredits.value = unwrapList(c)
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message || '載入失敗'
    }
  }

  function startEditShipping(rule) {
    editingShippingId.value = rule.id
    shipForm.value = ruleToShipForm(rule)
    loadError.value = ''
  }

  function cancelEditShipping() {
    editingShippingId.value = null
    shipForm.value = defaultShipForm()
    loadError.value = ''
  }

  function onShippingDragStart(index, event) {
    shippingDragIndex.value = index
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    }
  }

  function onShippingDragOver(index) {
    shippingDragOverIndex.value = index
  }

  function onShippingDragEnd() {
    shippingDragIndex.value = null
    shippingDragOverIndex.value = null
  }

  async function onShippingDrop(toIndex) {
    const fromIndex = shippingDragIndex.value
    onShippingDragEnd()
    if (fromIndex === null || fromIndex === toIndex || saving.value) return
    const previous = shippingRules.value.slice()
    const items = shippingRules.value.slice()
    const [moved] = items.splice(fromIndex, 1)
    items.splice(toIndex, 0, moved)
    shippingRules.value = items
    saving.value = true
    loadError.value = ''
    try {
      const res = await commerceSettingsApi.reorderShippingRules({ ids: items.map((rule) => rule.id) })
      const shipping = unwrapShipping(res)
      shippingRules.value = sortShippingRulesByPriority(shipping.items)
      ecpayLogistics.value = shipping.ecpayLogistics
    } catch (e) {
      shippingRules.value = previous
      loadError.value = e?.response?.data?.message || e.message || '排序儲存失敗'
    } finally {
      saving.value = false
    }
  }

  async function saveShipping() {
    saving.value = true
    loadError.value = ''
    try {
      const editing = shippingRules.value.find((rule) => rule.id === editingShippingId.value)
      const managed = isEcpayManagedRule(editing)
      let payload
      if (managed) {
        const active = shipForm.value.is_active !== false
        const feeRaw = shipForm.value.fee
        const feeEmpty = feeRaw === '' || feeRaw === null || feeRaw === undefined
        if (active && (feeEmpty || !/^\d+$/.test(String(feeRaw).trim()) || Number(feeRaw) <= 0)) {
          loadError.value = '啟用時運費須為大於 0 的整數'
          return
        }
        if (!active && !feeEmpty && (!/^\d+$/.test(String(feeRaw).trim()) || Number(feeRaw) <= 0)) {
          loadError.value = '運費須為大於 0 的整數'
          return
        }
        payload = {
          is_active: active,
          config: {
            fee: feeEmpty ? null : Number(String(feeRaw).trim()),
            managedBy: 'ecpay',
            logisticsSubtype: parseRuleConfig(editing.config).logisticsSubtype,
          },
        }
      } else {
        payload = {
          name: shipForm.value.name,
          rule_type: shipForm.value.rule_type,
          config: buildShippingConfig(shipForm.value),
          is_active: shipForm.value.is_active !== false,
        }
      }
      if (editingShippingId.value) {
        await commerceSettingsApi.updateShippingRule(editingShippingId.value, payload)
      } else {
        await commerceSettingsApi.createShippingRule(payload)
      }
      cancelEditShipping()
      await reload({ ensureEcpayShipping: true })
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message
    } finally {
      saving.value = false
    }
  }

  async function resetEcpayShipping() {
    if (!confirm('依目前物流類型重設寄送方式？已修改的運費金額、啟用狀態與順序會改回預設。')) return
    saving.value = true
    loadError.value = ''
    try {
      const res = await commerceSettingsApi.resetEcpayShippingRules()
      const shipping = unwrapShipping(res)
      shippingRules.value = sortShippingRulesByPriority(shipping.items)
      ecpayLogistics.value = shipping.ecpayLogistics
      cancelEditShipping()
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message
    } finally {
      saving.value = false
    }
  }

  async function removeShipping(id) {
    if (!confirm('刪除此運費規則？')) return
    await commerceSettingsApi.deleteShippingRule(id)
    if (editingShippingId.value === id) {
      cancelEditShipping()
    }
    await reload()
  }

  function startEditPromo(rule) {
    if (rule.rule_type !== 'commerce_cart_discount') {
      loadError.value = '此規則為舊版格式，請刪除後再以新表單新增。'
      return
    }
    editingPromoId.value = rule.id
    promoForm.value = ruleToPromoForm(rule)
    loadError.value = ''
  }

  function cancelEditPromo() {
    editingPromoId.value = null
    promoForm.value = defaultPromoForm()
    loadError.value = ''
  }

  async function savePromo() {
    const nameTrim = String(promoForm.value.name || '').trim()
    if (!nameTrim) {
      loadError.value = '請填寫規則名稱'
      return
    }
    saving.value = true
    loadError.value = ''
    try {
      const body = {
        rule_type: 'commerce_cart_discount',
        name: nameTrim,
        priority: Number(promoForm.value.priority) || 0,
        config: buildPromoConfig(promoForm.value),
        is_active: promoForm.value.is_active !== false,
        valid_from: toIsoDateStart(promoForm.value.validFrom),
        valid_to: toIsoDateEnd(promoForm.value.validTo),
      }
      if (editingPromoId.value) {
        await commerceSettingsApi.updatePromotionRule(editingPromoId.value, body)
      } else {
        await commerceSettingsApi.createPromotionRule(body)
      }
      cancelEditPromo()
      await reload()
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message
    } finally {
      saving.value = false
    }
  }

  async function removePromo(id) {
    await commerceSettingsApi.deletePromotionRule(id)
    if (editingPromoId.value === id) {
      cancelEditPromo()
    }
    await reload()
  }

  async function addB2b() {
    saving.value = true
    try {
      await commerceSettingsApi.upsertB2bTierPrice({
        variantId: b2bForm.value.variantId,
        memberTier: b2bForm.value.memberTier,
        unitPrice: b2bForm.value.unitPrice,
      })
      b2bForm.value = { variantId: '', memberTier: '', unitPrice: 0 }
      await reloadB2bPrices()
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message
    } finally {
      saving.value = false
    }
  }

  async function removeB2b(id) {
    if (!confirm('刪除此筆 B2B 價？')) return
    await commerceSettingsApi.deleteB2bTierPrice(id)
    await reloadB2bPrices()
  }

  /**
   * @param {Record<string, unknown>} limitsByTier - tierCode -> 額度（空白表示不限制）
   * @param {string[]} activeTierCodes - 要寫入的等級代碼順序（通常為啟用中會員等級）
   */
  async function saveAllTierCredits(limitsByTier, activeTierCodes) {
    saving.value = true
    loadError.value = ''
    try {
      for (const code of activeTierCodes) {
        const raw = limitsByTier?.[code]
        let lim = null
        if (raw !== '' && raw != null) {
          const s = typeof raw === 'string' ? raw.trim() : raw
          if (s !== '') {
            const n = Number(s)
            if (!Number.isFinite(n) || n < 0) {
              loadError.value = `「${code}」的月信用額度須為非負數字`
              return
            }
            lim = n
          }
        }
        await commerceSettingsApi.upsertMemberTierCredit({
          memberTier: code,
          monthlyCreditLimit: lim,
        })
      }
      await reloadTierCredits()
    } catch (e) {
      loadError.value = e?.response?.data?.message || e.message
    } finally {
      saving.value = false
    }
  }

  return {
    loadError,
    saving,
    shippingRules,
    ecpayLogistics,
    promoRules,
    b2bPrices,
    tierCredits,
    shipForm,
    editingShippingId,
    promoForm,
    editingPromoId,
    b2bForm,
    reload,
    reloadB2bPrices,
    reloadTierCredits,
    saveShipping,
    resetEcpayShipping,
    shippingDragIndex,
    shippingDragOverIndex,
    onShippingDragStart,
    onShippingDragOver,
    onShippingDragEnd,
    onShippingDrop,
    startEditShipping,
    cancelEditShipping,
    removeShipping,
    savePromo,
    startEditPromo,
    cancelEditPromo,
    removePromo,
    addB2b,
    removeB2b,
    saveAllTierCredits,
  }
}
